import moment from "moment";
import { AxiosError } from "axios";

export const getPageNumberForMessageId = (
  chalListData,
  targetMessageId,
  messagesPerPage = 30
) => {
  let totalMessagesProcessed = 0;

  // Iterate through each date group in chalListData
  for (let dateGroup of chalListData) {
    // Calculate how many messages are in this date group
    const messagesInGroup = dateGroup.messages.length;

    // Check if the target message is in this group
    const messageIndex = dateGroup.messages.findIndex(
      (msg) => msg._id === targetMessageId
    );

    if (messageIndex !== -1) {
      // Calculate the page number
      const absoluteMessagePosition = totalMessagesProcessed + messageIndex + 1;
      return Math.ceil(absoluteMessagePosition / messagesPerPage);
    }

    // Add the number of messages in this group to total processed
    totalMessagesProcessed += messagesInGroup;
  }

  // If message not found, return null or throw an error
  return null;
};

export function deleteMessageFromChatlist(chalListData, messageId) {
  // Create a deep copy of chalListData to avoid direct mutation
  const updatedChalListData = JSON.parse(JSON.stringify(chalListData));

  // Iterate through each date group
  for (let i = 0; i < updatedChalListData.length; i++) {
    const dateGroup = updatedChalListData[i];

    // Find the index of the message to be deleted
    const messageIndex = dateGroup.messages.findIndex(
      (msg) => msg._id === messageId
    );

    // If message is found
    if (messageIndex !== -1) {
      // Remove the message from the messages array
      dateGroup.messages.splice(messageIndex, 1);

      // Decrease the count of messages
      dateGroup.count -= 1;

      // If no messages left, remove the entire date group
      if (dateGroup.messages.length === 0) {
        updatedChalListData.splice(i, 1);
      }

      // Return the updated chalListData
      return updatedChalListData;
    }
  }

  // If message not found, return the original data
  return chalListData;
}

export function messageSeen(
  chalListData,
  messageId,
  isChannel,
  currentUser,
  oppositeUser
) {
  // Deep copy chalListData to avoid direct mutation
  const updatedChalListData = JSON.parse(JSON.stringify(chalListData));

  if (!isChannel) {
    // Iterate through the messages in the chalListData
    updatedChalListData.forEach((chat) => {
      chat.messages.forEach((message) => {
        // Update is_seen only if the message _id matches messageId
        if (
          message._id === messageId &&
          message.is_seen === false &&
          message.receiverId === currentUser?._id
        ) {
          message.is_seen = true;
        }

        // Check replies within the matched message
        if (
          message._id === messageId &&
          message.replies &&
          message.replies.length > 0
        ) {
          message.replies.forEach((reply) => {
            if (
              reply.is_seen === false &&
              reply.receiverId === currentUser?._id
            ) {
              reply.is_seen = true;
            }
          });
        }
      });
    });
  } else {
    // Handle the case when isChannel is true
    updatedChalListData.forEach((chat) => {
      chat.messages.forEach((message) => {
        // Add currentUser._id to readBy if message _id matches messageId
        if (
          message._id === messageId &&
          !message.readBy.includes(currentUser?._id)
        ) {
          message.readBy.push(currentUser?._id);
        }

        // Check replies within the matched message
        if (
          message._id === messageId &&
          message.replies &&
          message.replies.length > 0
        ) {
          message.replies.forEach((reply) => {
            if (!reply.readBy.includes(currentUser?._id)) {
              reply.readBy.push(currentUser?._id);
            }
          });
        }
      });
    });
  }

  return updatedChalListData;
}

export const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");

export function findPageNumber(data, messageId, pageSize = 30) {
  console.warn(messageId, "messageIdmessageId");
  // Flatten all messages across reversed days into a single array
  const allMessages = data.flatMap((day) =>
    day.messages.map((message) => ({ dayId: day._id, message }))
  );

  // Reverse the order of days without mutating the original array
  const reversedData = [...allMessages].reverse();

  // Find the index of the message with the given _id
  const messageIndex = reversedData.findIndex(
    (entry) => entry.message._id == messageId
  );

  if (messageIndex === -1) {
    return "Message not found";
  }

  // Calculate the page number (1-based index)
  const pageNumber = Math.floor(messageIndex / pageSize) + 1;
  console.warn(pageNumber, "messageIdmessageId");

  return pageNumber;
}

export function unsanitizeText(sanitizedText) {
  return sanitizedText
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");
}

export function populateChatUserData(
  sortedDateGroups,
  currentUser,
  targetUser
) {
  return sortedDateGroups.map((group) => {
    const updatedMessages = group.messages.map((message) => {
      // Skip processing if receiverInfo and senderInfo already exist
      if (message.receiverInfo && message.senderInfo) {
        return message;
      }

      const updatedMessage = { ...message };

      // Add receiver information if not already present
      if (!updatedMessage.receiverInfo) {
        if (updatedMessage.receiverId === currentUser._id) {
          updatedMessage.receiverInfo = [{ ...currentUser }];
        } else if (updatedMessage.receiverId === targetUser._id) {
          updatedMessage.receiverInfo = [{ ...targetUser }];
        }
      }

      // Add sender information if not already present
      if (!updatedMessage.senderInfo) {
        if (updatedMessage.senderId === targetUser._id) {
          updatedMessage.senderInfo = { ...targetUser };
        } else if (updatedMessage.senderId === currentUser._id) {
          updatedMessage.senderInfo = { ...currentUser };
        }
      }

      return updatedMessage;
    });

    return { ...group, messages: updatedMessages };
  });
}

export const scrollToBottomByID = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollTop = element.scrollHeight;
  }
};

export const getIsChannel = () => {
  const userData = localStorage.getItem("current_userdata");
  if (!userData) return true; // Default to true if no data

  try {
    const { username } = JSON.parse(userData) || {};
    return !username;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return true; // Assume true in case of error
  }
};

export const addMessageLocally = (
  messageList,
  setMessageList,
  newMessageData,
  currentUser,
  recipientUser
) => {
  // Guard clause for invalid input
  if (!messageList || !Array.isArray(messageList)) return;

  // Get today's date as ID
  const todayId = moment().format("YYYY-MM-DD");

  // Check if today's date entry exists
  const todayEntryExists = messageList.some((entry) => entry._id === todayId);

  // Create a new array to avoid direct mutation
  let updatedMessageList;

  if (todayEntryExists) {
    // Update existing entry for today
    updatedMessageList = messageList.map((entry) =>
      entry._id === todayId
        ? {
            ...entry,
            messages: [
              // The other side's messages are read now; the user's own keep their
              // seen state, which drives the seen ticks
              ...entry.messages.map((msg) =>
                msg.senderId === currentUser?._id ? msg : { ...msg, is_seen: true }
              ),
              // Add new message
              newMessageData,
            ],
            count: entry.count + 1,
          }
        : entry
    );
  } else {
    // Create new entry for today
    updatedMessageList = [
      ...messageList,
      {
        _id: todayId,
        messages: [newMessageData],
        count: 1,
      },
    ];
  }

  // Process data to include user information
  const processedMessageList = populateChatUserData(
    updatedMessageList,
    currentUser,
    recipientUser
  );

  // Update state with processed data
  setMessageList(processedMessageList);
};

// Records the message card's current size on the card itself (as CSS variables) just before it
// switches to edit mode, so the editor opens at the same width and height as the message.
// `event` is the click event of the Edit button, which sits inside the card.
export const lockEditCardSize = (event) => {
  const card = event?.currentTarget?.closest?.(".chat-card");
  if (!card) return;
  card.style.setProperty("--edit-card-width", `${card.offsetWidth}px`);
  card.style.setProperty("--edit-card-height", `${card.offsetHeight}px`);
};

// Longest message the composer will send. Longer text used to be cut at 7,000 characters with a
// trailing "..." and no warning; now it is sent in full up to this limit, and refused with a
// message beyond it.
export const MAX_MESSAGE_LENGTH = 15000;
export const MESSAGE_TOO_LONG_TEXT = `Message is too long. The limit is ${MAX_MESSAGE_LENGTH.toLocaleString()} characters.`;

export const is24HoursOld = (timestamp) => {
  const givenTime = moment(timestamp);
  const twentyFourHoursAgo = moment().subtract(24, "hours");
  return givenTime.isBefore(twentyFourHoursAgo);
};

export const handleApiError = (error, customMessage) => {
  // Check if it's a canceled request error
  if (error instanceof AxiosError && error.code === "ERR_CANCELED") {
    // Silently ignore canceled requests
    return;
  }

  if (error instanceof AxiosError) {
    if (error.response?.data?.message) {
      console.error(error.response.data.message);
    } else if (error.request) {
      // No response received (Network error, server down, CORS issues)
      console.error("No response from the server. Please try again later.");
    } else {
      // Other Axios errors (bad request, client-side issues)
      console.error(
        customMessage || "Something went wrong while processing your request."
      );
    }
  } else if (error instanceof Error) {
    // Standard JavaScript errors (TypeError, ReferenceError, etc.)
    console.error(error.message || "An unexpected error occurred.");
  } else {
    // Unknown error (rare cases)
    console.error(customMessage || "An unknown error occurred.");
  }
};

export const localStorageUtil = {
  setItem(key, value) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      // If it's already a string, store directly; otherwise serialize it.
      const toStore = typeof value === "string" ? value : JSON.stringify(value);
      localStorage.setItem(key, toStore);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  getItem(key, defaultValue = null) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return defaultValue;
      }

      // Simple heuristic: if it starts with { or [ it's probably JSON
      const firstChar = stored.trim()[0];
      if (firstChar === "{" || firstChar === "[") {
        try {
          return JSON.parse(stored);
        } catch {
          // not valid JSON after all
        }
      }
      return stored;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return defaultValue;
    }
  },

  removeItem(key) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },

  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  keyExists(key) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error("Error checking existence in localStorage:", error);
      return false;
    }
  },
};

export const countEmojiReactions = (data) => {
  return Object.values(
    data.reduce((acc, { emoji, username, userId, _id }) => {
      const extractedUsername = username || (userId && userId.username) || null;
      if (!acc[emoji]) {
        acc[emoji] = { emoji, username: [], count: 0, _id: [] };
      }
      acc[emoji].username.push(extractedUsername);
      acc[emoji]._id.push(_id);
      acc[emoji].count += 1;
      return acc;
    }, {})
  );
};

export const mergeMessages = (newMessages, existingMessages) => {
  // Pre-process date parsing for sorting optimization
  const parseDate = (dateStr) => {
    // Use timestamp for faster comparisons
    return new Date(dateStr).getTime();
  };

  // Create indexed structure for existing data (day groups and messages)
  const dayGroups = Object.create(null); // Faster than regular object or Map for known string keys
  const messagesByDay = Object.create(null);

  // Process old data first (single pass)
  for (let i = 0; i < newMessages.length; i++) {
    const day = newMessages[i];
    dayGroups[day._id] = {
      _id: day._id,
      messages: [], // Placeholder - will populate after message processing
      count: 0, // Will calculate after merging
      parsedDate: parseDate(day._id), // Pre-parse date for sorting
    };

    // Create ultra-fast lookup for messages
    messagesByDay[day._id] = Object.create(null);

    // Process all messages in this day
    for (let j = 0; j < day.messages.length; j++) {
      const msg = day.messages[j];
      messagesByDay[day._id][msg._id] = {
        ...msg,
        parsedDate: parseDate(msg.createdAt), // Pre-parse date for sorting
      };
    }
  }

  // Process new data (single pass)
  for (let i = 0; i < existingMessages.length; i++) {
    const newDay = existingMessages[i];

    // If day doesn't exist yet, create it
    if (!dayGroups[newDay._id]) {
      dayGroups[newDay._id] = {
        _id: newDay._id,
        messages: [], // Placeholder
        count: 0, // Will calculate later
        parsedDate: parseDate(newDay._id),
      };
      messagesByDay[newDay._id] = Object.create(null);
    }

    // Process all new messages
    for (let j = 0; j < newDay.messages.length; j++) {
      const newMsg = newDay.messages[j];

      // Update or add message
      if (messagesByDay[newDay._id][newMsg._id]) {
        // Update existing message (merge properties)
        Object.assign(messagesByDay[newDay._id][newMsg._id], newMsg);
        // Make sure parsed date is updated if createdAt changed
        messagesByDay[newDay._id][newMsg._id].parsedDate = parseDate(
          newMsg.createdAt
        );
      } else {
        // Add new message
        messagesByDay[newDay._id][newMsg._id] = {
          ...newMsg,
          parsedDate: parseDate(newMsg.createdAt),
        };
      }
    }
  }

  // Final assembly and sorting (single pass)
  const result = [];

  // Get all day IDs
  const dayIds = Object.keys(dayGroups);

  // For each day, assemble final messages array
  for (let i = 0; i < dayIds.length; i++) {
    const dayId = dayIds[i];
    const dayMsgs = Object.values(messagesByDay[dayId]);

    // Sort messages by creation time using pre-parsed timestamps (much faster than parsing in comparator)
    dayMsgs.sort((a, b) => a.parsedDate - b.parsedDate);

    // Cleanup: remove parsed date helper property from final output
    for (let j = 0; j < dayMsgs.length; j++) {
      delete dayMsgs[j].parsedDate;
    }

    // Assemble day group
    dayGroups[dayId].messages = dayMsgs;
    dayGroups[dayId].count = dayMsgs.length;

    // Add to result array
    result.push(dayGroups[dayId]);
  }

  // Sort day groups by date using pre-parsed timestamps
  result.sort((a, b) => a.parsedDate - b.parsedDate);

  // Cleanup: remove parsed date helper property from final output
  for (let i = 0; i < result.length; i++) {
    delete result[i].parsedDate;
  }

  return result;
};

export const getMentionedUserIds = (message, users) => {
  const mentionedUsernames = [...message.matchAll(/@(\w+)/g)].map(
    (match) => match[1]
  );

  return mentionedUsernames
    .map((username) => {
      const user = users.find((user) => user.username === username);
      return user ? user._id : null;
    })
    .filter(Boolean);
};

export const getMentionedUserEmail = (message, users) => {
  // Extract mentioned usernames from the message using regex
  const mentionedUsernames = [...message.matchAll(/@(\w+)/g)].map(
    (match) => match[1]
  );

  // Find the corresponding _id for each mentioned username
  const mentionedUserIds = mentionedUsernames
    .map((username) => {
      const user = users.find((user) => user.username === username);
      return user ? user.email : null;
    })
    .filter((email) => email); // Remove null values (for unmatched usernames)

  console.warn(mentionedUserIds, "mentionedUserIds");

  return mentionedUserIds;
};

export const extractFileName = (filePath) => {
  const normalizedPath = filePath.replace(/\\/g, "/");
  const fullFileName = normalizedPath.split("/").pop();

  const extension = fullFileName.split(".").pop();
  const baseName = fullFileName.slice(0, -(extension.length + 1));

  // Truncate the base name to 15 characters
  const truncatedName =
    baseName.length > 15 ? baseName.slice(0, 15) + "..." : baseName;

  return `${truncatedName}.${extension}`;
};

export const removePinMessage = (pinMessageId, data) => {
  // Input validation
  if (!pinMessageId || typeof pinMessageId !== "string") {
    throw new Error("Pin message ID must be a non-empty string");
  }

  if (!data || typeof data !== "object" || !Array.isArray(data.pinmessage)) {
    throw new Error("Data must be an object with a pinmessage array");
  }

  // Create a shallow copy of the data object to avoid mutation
  const updatedData = { ...data };

  // Use findIndex for O(n) performance and filter for immutability
  const messageIndex = data.pinmessage.findIndex(
    (msg) => msg._id === pinMessageId
  );

  if (messageIndex === -1) {
    // Message not found, return original data unchanged
    return updatedData;
  }

  // Create new array without the specified message
  // Using slice for better performance than filter for single item removal
  updatedData.pinmessage = [
    ...data.pinmessage.slice(0, messageIndex),
    ...data.pinmessage.slice(messageIndex + 1),
  ];

  // Update pinnedMessageCount
  updatedData.pinnedMessageCount = updatedData.pinmessage.length;

  return updatedData;
};

export const removeByIdFromObjectArray1D = (items, targetId) => {
  return items.filter((item) => item._id !== targetId);
};

export const requestNotificationPermission = () => {
  if ("Notification" in window && Notification.permission !== "granted") {
    Notification.requestPermission();
  }
};

export const addMuteStatus = (data, muteinfo, basedProperty) => {
  const muteSet = new Set(muteinfo[basedProperty]);
  return data.map((user) => ({
    ...user,
    isMutedByMe: muteSet.has(user._id),
  }));
};

export const updateDataById = (data, id, updates, shouldDelete = false) => {
  // If shouldDelete is true, filter out the matching object
  if (shouldDelete) {
    return data.filter((item) => item._id !== id);
  }

  // Otherwise, update the matching object
  return data.map((item) => (item._id === id ? { ...item, ...updates } : item));
};
